import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import StorefrontLayout from "components/storefront/StorefrontLayout";
import { useCart } from "context/CartContext";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { get } from "lib/api";
import { firstMediaUrl } from "lib/media";
import { currencyForCompany } from "lib/companyProfile";
import { getTextLocale } from "lib/storefront";
import { storefrontThemeForTenant } from "lib/storefrontTheme";
import logo from "assets/images/logos/ShortLogo.png";
import useStorefrontCopy from "hooks/useStorefrontCopy";

const BRAND = {
  ink: "var(--storefront-ink, #111827)",
  muted: "var(--storefront-muted, #667085)",
  line: "var(--storefront-line, rgba(17,24,39,0.1))",
  canvas: "var(--storefront-canvas, #f7f8fa)",
  red: "var(--storefront-accent, #d7261e)",
  orange: "#f97316",
};

function productImage(product) {
  return firstMediaUrl(product?.images);
}

function priceText(value, currency = "USD") {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "Call for pricing";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function resolvePrice(product) {
  const unitPrice = Number(product?.unitPrice);
  if (Number.isFinite(unitPrice) && unitPrice > 0) return product.unitPrice;
  const levels =
    product?.priceLevels && typeof product.priceLevels === "object"
      ? product.priceLevels
      : {};
  return levels.retail ?? levels.Retail ?? levels.RETAIL ?? product?.unitPrice;
}

function priceBadge(product) {
  const tier = String(product?.priceTierApplied || "").trim();
  if (tier && tier.toLowerCase() !== "retail")
    return `${tier.replaceAll("_", " ")} pricing`;
  const scope = String(product?.pricingScope || "").trim();
  if (scope)
    return scope === "customer"
      ? "Customer distributor pricing"
      : "Distributor pricing";
  return "";
}

function normalizeTransmissions(raw) {
  const rows = raw?.data || raw || [];
  return (Array.isArray(rows) ? rows : [])
    .map((row) => row?.attributes?.name || row?.name || row?.label || "")
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

function ProductCard({
  product,
  onOpen,
  onAdd,
  onSignIn,
  isMobile,
  inCartQuantity,
  currency,
  canOrder,
}) {
  const image = productImage(product);
  const unitPrice = resolvePrice(product);
  const hasPrice = Number(unitPrice) > 0;
  const coreCharge = Number(product.coreCharge || 0);
  const appliedPriceBadge = priceBadge(product);
  if (isMobile) {
    return (
      <Box
        sx={{
          borderBottom: `1px solid ${BRAND.line}`,
          bgcolor: "transparent",
          py: 2,
        }}
      >
        <MDBox display="flex" gap={1.5}>
          <Box
            onClick={onOpen}
            sx={{
              width: 118,
              height: 132,
              flexShrink: 0,
              borderRadius: 1,
              bgcolor: "#f0f1f2",
              border: `1px solid ${BRAND.line}`,
              overflow: "hidden",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            {image ? (
              <Box
                component="img"
                src={image}
                alt={product.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  p: 1,
                }}
              />
            ) : (
              <Icon sx={{ fontSize: 40, color: "#98a2b3" }}>
                precision_manufacturing
              </Icon>
            )}
          </Box>
          <MDBox
            sx={{
              minWidth: 0,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={0.75}
              mb={0.7}
            >
              <MDTypography
                variant="caption"
                sx={{
                  color: BRAND.muted,
                  fontFamily: "monospace",
                  fontWeight: 900,
                  letterSpacing: 0,
                }}
              >
                {product.sku || "Pending"}
              </MDTypography>
              {product.status === "available" ? (
                <MDTypography
                  variant="caption"
                  sx={{ color: "#147a5c", fontWeight: 900 }}
                >
                  READY
                </MDTypography>
              ) : null}
            </Stack>
            <MDTypography
              onClick={onOpen}
              variant="button"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                color: BRAND.ink,
                fontWeight: 900,
                lineHeight: 1.18,
                fontSize: "0.98rem",
                cursor: "pointer",
              }}
            >
              {product.name}
            </MDTypography>
            {appliedPriceBadge ? (
              <MDTypography
                variant="caption"
                sx={{
                  color: "#147a5c",
                  fontWeight: 900,
                  display: "block",
                  mt: 0.5,
                }}
              >
                {appliedPriceBadge}
              </MDTypography>
            ) : null}
            <MDBox mt="auto">
              <MDTypography
                variant="h6"
                sx={{
                  color: hasPrice ? BRAND.red : BRAND.ink,
                  fontWeight: 900,
                  lineHeight: 1.15,
                  mb: coreCharge > 0 ? 0.2 : 1,
                }}
              >
                {priceText(unitPrice, currency)}
              </MDTypography>
              {coreCharge > 0 ? (
                <MDTypography
                  variant="caption"
                  sx={{ color: BRAND.muted, display: "block", mb: 0.9 }}
                >
                  Core {priceText(coreCharge, currency)}
                </MDTypography>
              ) : null}
              <Stack
                direction="row"
                spacing={0}
                sx={{ border: `1px solid ${BRAND.line}` }}
              >
                <MDButton
                  variant="text"
                  sx={{
                    flex: 1,
                    borderRadius: 0.75,
                    minHeight: 38,
                    color: hasPrice ? BRAND.red : BRAND.ink,
                    fontWeight: 900,
                    boxShadow: "none",
                    borderRight: `1px solid ${BRAND.line}`,
                    "&:hover": {
                      bgcolor: "rgba(17,24,39,0.03)",
                      boxShadow: "none",
                    },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (hasPrice && canOrder) onAdd();
                    else if (hasPrice) onSignIn();
                    else onOpen();
                  }}
                >
                  {hasPrice
                    ? canOrder
                      ? inCartQuantity > 0
                        ? `Added ${inCartQuantity}`
                        : "Add"
                      : "Sign in"
                    : "Details"}
                </MDButton>
                <MDButton
                  variant="text"
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 38,
                    p: 0,
                    borderRadius: 0.75,
                    color: BRAND.ink,
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpen();
                  }}
                  aria-label="View details"
                >
                  <Icon>arrow_forward</Icon>
                </MDButton>
              </Stack>
            </MDBox>
          </MDBox>
        </MDBox>
      </Box>
    );
  }
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: isMobile ? 2 : "18px",
        border: `1px solid ${BRAND.line}`,
        boxShadow: isMobile
          ? "0 10px 28px rgba(17,24,39,0.06)"
          : "0 16px 44px rgba(17,24,39,0.06)",
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: "#fff",
        transition:
          "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: isMobile ? "none" : "translateY(-5px)",
          borderColor: "rgba(215,38,30,0.28)",
          boxShadow: "0 22px 60px rgba(17,24,39,0.1)",
        },
      }}
      onClick={onOpen}
    >
      {image ? (
        <CardMedia
          component="img"
          image={image}
          alt={product.name}
          sx={{
            height: isMobile ? 178 : 230,
            objectFit: "cover",
            bgcolor: "#f3f4f6",
          }}
        />
      ) : (
        <MDBox
          height={isMobile ? 178 : 230}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ bgcolor: "#eef1f4" }}
        >
          <Icon sx={{ fontSize: isMobile ? 44 : 58, color: "#98a2b3" }}>
            precision_manufacturing
          </Icon>
        </MDBox>
      )}
      <CardContent
        sx={{
          p: isMobile ? 1.6 : 2.5,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1.2}
          mb={0.75}
        >
          <MDTypography
            variant="h6"
            sx={{
              color: BRAND.ink,
              fontSize: isMobile ? "0.98rem" : "1.02rem",
              lineHeight: 1.3,
              fontWeight: 900,
            }}
          >
            {product.name}
          </MDTypography>
          {product.status === "available" ? (
            <Chip
              label="Available"
              size="small"
              sx={{
                bgcolor: "rgba(34,197,94,0.1)",
                color: "#167b3f",
                fontWeight: 800,
                borderRadius: "999px",
              }}
            />
          ) : null}
        </MDBox>
        {appliedPriceBadge ? (
          <Chip
            label={appliedPriceBadge}
            size="small"
            sx={{
              alignSelf: "flex-start",
              bgcolor: "rgba(20,122,92,0.1)",
              color: "#147a5c",
              fontWeight: 900,
              mb: 1,
            }}
          />
        ) : null}
        <MDTypography
          variant="caption"
          sx={{ color: BRAND.muted, fontFamily: "monospace", mb: 2 }}
        >
          SKU {product.sku || "Pending"}
        </MDTypography>
        <MDBox mt="auto">
          <MDTypography
            variant="h6"
            sx={{
              color: hasPrice ? BRAND.red : BRAND.ink,
              fontWeight: 900,
              fontSize: hasPrice ? "1.08rem" : "0.9rem",
              mb: 1.25,
            }}
          >
            {priceText(unitPrice, currency)}
          </MDTypography>
          {coreCharge > 0 ? (
            <MDTypography
              variant="caption"
              sx={{ color: BRAND.muted, display: "block", mb: 1.25 }}
            >
              Refundable core deposit {priceText(coreCharge, currency)}
            </MDTypography>
          ) : null}
          {Array.isArray(product.transmissions) &&
          product.transmissions.length ? (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mb: 1.25, overflowX: "auto", pb: 0.25 }}
            >
              {product.transmissions.slice(0, 3).map((transmission) => (
                <Chip
                  key={transmission}
                  size="small"
                  label={transmission}
                  sx={{ flexShrink: 0, fontWeight: 800 }}
                />
              ))}
            </Stack>
          ) : null}
          <Stack direction="row" spacing={1}>
            {hasPrice ? (
              <MDButton
                variant="contained"
                fullWidth
                sx={{
                  bgcolor: BRAND.red,
                  color: "#fff",
                  borderRadius: 1,
                  boxShadow: "none",
                  py: 1,
                  fontSize: "0.78rem",
                  fontWeight: 900,
                  "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (canOrder) onAdd();
                  else onSignIn();
                }}
              >
                {canOrder
                  ? inCartQuantity > 0
                    ? `In cart: ${inCartQuantity}`
                    : "Add"
                  : "Sign in to order"}
              </MDButton>
            ) : null}
            <MDButton
              variant={hasPrice ? "outlined" : "contained"}
              fullWidth
              sx={{
                bgcolor: hasPrice ? "#fff" : BRAND.ink,
                color: hasPrice ? BRAND.ink : "#fff",
                borderColor: "rgba(17,24,39,0.14)",
                borderRadius: 1,
                boxShadow: "none",
                py: 1,
                fontSize: "0.78rem",
                fontWeight: 900,
                "&:hover": {
                  borderColor: BRAND.red,
                  bgcolor: hasPrice ? "#fff" : BRAND.red,
                  boxShadow: "none",
                },
              }}
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
            >
              View
            </MDButton>
          </Stack>
        </MDBox>
      </CardContent>
    </Card>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    images: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    name: PropTypes.string,
    status: PropTypes.string,
    sku: PropTypes.string,
    unitPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    coreCharge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priceLevels: PropTypes.shape({}),
    priceTierApplied: PropTypes.string,
    pricingScope: PropTypes.string,
    markupPercentApplied: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    transmissions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onSignIn: PropTypes.func,
  isMobile: PropTypes.bool,
  inCartQuantity: PropTypes.number,
  currency: PropTypes.string,
  canOrder: PropTypes.bool,
};

ProductCard.defaultProps = {
  isMobile: false,
  inCartQuantity: 0,
  currency: "USD",
  canOrder: false,
  onSignIn: () => {},
};

export default function ProductCatalog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale } = useStorefrontCopy();
  const { tenant } = useStorefrontTenant();
  const storefrontTheme = storefrontThemeForTenant(tenant, locale);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = String(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTransmission, setSelectedTransmission] = useState("");
  const isMobileRoute =
    location.pathname === "/m/catalog" ||
    location.pathname.startsWith("/m/catalog/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;
  const { addItem, lines, itemCount } = useCart();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const canOrder = !!token;
  const companyKey = String(
    storefrontTenant || activeCompany || "",
  ).toLowerCase();
  const textLocale = getTextLocale();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(q), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "catalog-products",
      debouncedSearch,
      companyKey,
      textLocale,
      token ? "auth" : "public",
    ],
    queryFn: () =>
      get("/api/storefront/catalog", {
        q: debouncedSearch || undefined,
        sort: "name:asc",
        pagination: { pageSize: 100 },
      }),
    staleTime: 30000,
  });
  const currency =
    data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);

  const products = useMemo(
    () =>
      (data?.data || []).map((item) => {
        const attrs = item.attributes || item;
        return {
          id: item.id,
          documentId: item.documentId || item.id,
          sku: attrs.sku,
          name: attrs.name,
          description: attrs.description,
          images: attrs.images,
          unitPrice: attrs.unitPrice,
          coreCharge: attrs.coreCharge,
          priceLevels: attrs.priceLevels,
          priceTierApplied: attrs.priceTierApplied,
          pricingScope: attrs.pricingScope,
          markupPercentApplied: attrs.markupPercentApplied,
          transmissions: normalizeTransmissions(attrs.transmissions),
          status: attrs.status,
        };
      }),
    [data],
  );

  const cartQuantityByDocumentId = useMemo(() => {
    const map = new Map();
    (lines || []).forEach((line) => {
      map.set(String(line.documentId), Number(line.qty || 0));
    });
    return map;
  }, [lines]);

  const transmissionOptions = useMemo(() => {
    const set = new Set();
    products.forEach((product) => {
      (product.transmissions || []).forEach((transmission) =>
        set.add(transmission),
      );
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const visibleProducts = useMemo(() => {
    if (!selectedTransmission) return products;
    return products.filter((product) =>
      (product.transmissions || []).some(
        (transmission) =>
          transmission.toLowerCase() === selectedTransmission.toLowerCase(),
      ),
    );
  }, [products, selectedTransmission]);

  const updateSearch = (value) => {
    const next = String(value || "");
    const params = new URLSearchParams(searchParams);
    if (next.trim()) params.set("q", next);
    else params.delete("q");
    setSearchParams(params, { replace: true });
  };

  const addProductToCart = (product) => {
    if (!canOrder) {
      navigate("/m/authentication/sign-in?next=/m/catalog");
      return;
    }
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: Number(resolvePrice(product) || 0),
        coreCharge:
          product.coreCharge != null ? Number(product.coreCharge || 0) : null,
        imageUrl: productImage(product),
      },
      1,
    );
  };

  if (!isMobileRoute && storefrontTheme.preset === "industrial") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor:
              "radial-gradient(circle at 92% 5%, rgba(181,71,8,0.14), transparent 34%), #080B0E",
            color: "#fff",
            py: 3,
          }}
        >
          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "260px minmax(0,1fr)" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  position: { lg: "sticky" },
                  top: 104,
                  bgcolor: "#050608",
                  minHeight: { lg: "calc(100vh - 132px)" },
                  display: "grid",
                  gridTemplateRows: "auto 1fr auto",
                  p: 2,
                  boxShadow: "0 28px 90px rgba(0,0,0,0.25)",
                }}
              >
                <Box>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.48)",
                      fontFamily: "monospace",
                      fontWeight: 900,
                      letterSpacing: "0.14em",
                    }}
                  >
                    PRODUCT MATRIX
                  </MDTypography>
                  <MDTypography
                    variant="h3"
                    sx={{ color: "#fff", fontWeight: 950, lineHeight: 0.98, mt: 1 }}
                  >
                    {t("catalog.title", "Products")}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.62)", lineHeight: 1.6, mt: 1.25 }}
                  >
                    Use the global command search above or filter by platform.
                  </MDTypography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.48)", fontWeight: 900 }}
                  >
                    PLATFORM REGISTER
                  </MDTypography>
                  <Box sx={{ mt: 1 }}>
                    {["", ...transmissionOptions].map((transmission) => {
                      const active = selectedTransmission === transmission;
                      const label = transmission || `All platforms (${products.length})`;
                      return (
                        <Box
                          key={label}
                          component="button"
                          type="button"
                          onClick={() => setSelectedTransmission(transmission)}
                          sx={{
                            width: "100%",
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            gap: 1,
                            alignItems: "center",
                            bgcolor: active ? "rgba(181,71,8,0.18)" : "transparent",
                            color: active ? "#fff" : "rgba(255,255,255,0.68)",
                            border: 0,
                            borderLeft: active ? `3px solid ${storefrontTheme.accent}` : "3px solid transparent",
                            textAlign: "left",
                            p: 1,
                            cursor: "pointer",
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{ color: "inherit", fontWeight: 900 }}
                          >
                            {label}
                          </MDTypography>
                          <Icon sx={{ fontSize: 18, color: "inherit" }}>chevron_right</Icon>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Box sx={{ mt: 3, borderTop: "1px solid rgba(255,255,255,0.12)", pt: 2 }}>
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.48)", fontWeight: 900 }}
                  >
                    Showing
                  </MDTypography>
                  <MDTypography variant="h4" sx={{ color: "#fff", fontWeight: 950 }}>
                    {visibleProducts.length}
                  </MDTypography>
                </Box>
              </Box>

              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.94)",
                  color: BRAND.ink,
                  boxShadow: "0 34px 100px rgba(0,0,0,0.22)",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1.25fr) 140px 160px 140px 150px",
                    gap: 1.5,
                    alignItems: "center",
                    px: 2,
                    py: 1.25,
                    bgcolor: "#F7F8F5",
                    borderBottom: `1px solid ${BRAND.line}`,
                  }}
                >
                  {["Product", "SKU", "Platform", "Price", "Dispatch"].map((label) => (
                    <MDTypography
                      key={label}
                      variant="caption"
                      sx={{ color: BRAND.muted, fontFamily: "monospace", fontWeight: 900 }}
                    >
                      {label}
                    </MDTypography>
                  ))}
                </Box>

                {isLoading ? (
                  <MDBox textAlign="center" py={8}>
                    <MDTypography sx={{ color: BRAND.muted }}>Loading products...</MDTypography>
                  </MDBox>
                ) : visibleProducts.length === 0 ? (
                  <MDBox textAlign="center" py={8}>
                    <MDTypography sx={{ color: BRAND.muted }}>
                      {t("catalog.empty", "No products are available right now.")}
                    </MDTypography>
                  </MDBox>
                ) : (
                  visibleProducts.map((product, index) => {
                    const unitPrice = resolvePrice(product);
                    const hasPrice = Number(unitPrice) > 0;
                    return (
                      <Box
                        key={product.documentId || product.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "74px minmax(0,1fr)",
                            md: "minmax(0,1.25fr) 140px 160px 140px 150px",
                          },
                          gap: 1.5,
                          alignItems: "center",
                          px: 2,
                          py: 1.3,
                          borderBottom: `1px solid ${BRAND.line}`,
                          "&:hover": { bgcolor: "rgba(181,71,8,0.055)" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                          <Box
                            component={productImage(product) ? "img" : "div"}
                            src={productImage(product) || undefined}
                            sx={{
                              width: 68,
                              height: 58,
                              objectFit: "contain",
                              bgcolor: "#EDF0ED",
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <MDTypography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontFamily: "monospace", fontWeight: 900 }}
                            >
                              ROW {String(index + 1).padStart(2, "0")}
                            </MDTypography>
                            <MDTypography
                              variant="button"
                              sx={{ display: "block", color: BRAND.ink, fontWeight: 950, lineHeight: 1.2 }}
                            >
                              {product.name}
                            </MDTypography>
                          </Box>
                        </Box>
                        <MDTypography
                          variant="caption"
                          sx={{ color: BRAND.ink, fontFamily: "monospace", fontWeight: 900, display: { xs: "none", md: "block" } }}
                        >
                          {product.sku || "PENDING"}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{ color: BRAND.muted, fontWeight: 800, display: { xs: "none", md: "block" } }}
                        >
                          {(product.transmissions || [])[0] || "Universal"}
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          sx={{ color: hasPrice ? BRAND.red : BRAND.ink, fontWeight: 950, display: { xs: "none", md: "block" } }}
                        >
                          {priceText(unitPrice, currency)}
                        </MDTypography>
                        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.75 }}>
                          <MDButton
                            variant="text"
                            sx={{ color: BRAND.ink, borderRadius: 0, fontWeight: 950, px: 1 }}
                            onClick={() => navigate(toPath(`/catalog/${product.documentId}`))}
                          >
                            Detail
                          </MDButton>
                          {hasPrice ? (
                            <MDButton
                              variant="contained"
                              sx={{
                                bgcolor: BRAND.red,
                                color: "#fff",
                                borderRadius: 0,
                                fontWeight: 950,
                                boxShadow: "none",
                              }}
                              onClick={() =>
                                canOrder
                                  ? addProductToCart(product)
                                  : navigate("/authentication/sign-in?next=/catalog")
                              }
                            >
                              {canOrder ? "Add" : "Sign in"}
                            </MDButton>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (!isMobileRoute && storefrontTheme.preset === "commerce") {
    const spotlight = visibleProducts[0];
    const shelf = visibleProducts.slice(1);

    return (
      <StorefrontLayout maxWidth={null}>
        <Box sx={{ minHeight: "100vh", bgcolor: "#F7F9FC", py: 3 }}>
          <Container maxWidth="xl">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "310px minmax(0,1fr)" },
                gap: 2.5,
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  position: { lg: "sticky" },
                  top: 106,
                  bgcolor: "#fff",
                  p: 2.5,
                  boxShadow: "0 22px 70px rgba(16,24,40,0.08)",
                }}
              >
                <MDTypography
                  variant="h2"
                  sx={{ color: BRAND.ink, fontWeight: 950, lineHeight: 0.95 }}
                >
                  Browse
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{ color: BRAND.muted, lineHeight: 1.65, mt: 1.25 }}
                >
                  Choose a product lane below. Search stays in the marketplace header.
                </MDTypography>
                <Box sx={{ mt: 2.5, display: "grid", gap: 1 }}>
                  {["", ...transmissionOptions].map((transmission) => {
                    const active = selectedTransmission === transmission;
                    const label = transmission || `All products (${products.length})`;
                    return (
                      <MDButton
                        key={label}
                        variant="text"
                        sx={{
                          justifyContent: "space-between",
                          color: active ? "#fff" : BRAND.ink,
                          bgcolor: active ? BRAND.ink : "#F7F9FC",
                          borderRadius: 2,
                          minHeight: 44,
                          fontWeight: 900,
                          px: 1.5,
                          "&:hover": {
                            bgcolor: active ? BRAND.ink : `${BRAND.red}14`,
                          },
                        }}
                        onClick={() => setSelectedTransmission(transmission)}
                      >
                        {label}
                        <Icon fontSize="small">arrow_forward</Icon>
                      </MDButton>
                    );
                  })}
                </Box>
              </Box>

              <Box>
                {spotlight ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "minmax(0,1.1fr) minmax(320px,0.9fr)" },
                      gap: 0,
                      bgcolor: "#fff",
                      boxShadow: "0 32px 90px rgba(16,24,40,0.11)",
                      mb: 2.5,
                    }}
                  >
                    <Box
                      component={productImage(spotlight) ? "img" : "div"}
                      src={productImage(spotlight) || undefined}
                      sx={{
                        width: "100%",
                        minHeight: { xs: 300, md: 520 },
                        objectFit: "contain",
                        bgcolor: "#EEF4F8",
                      }}
                    />
                    <Box sx={{ p: { xs: 2.5, md: 4 }, display: "grid", alignContent: "center" }}>
                      <MDTypography
                        variant="caption"
                        sx={{ color: BRAND.red, fontWeight: 900 }}
                      >
                        MARKETPLACE PICK
                      </MDTypography>
                      <MDTypography
                        variant="h1"
                        sx={{
                          color: BRAND.ink,
                          fontWeight: 950,
                          lineHeight: 0.96,
                          fontSize: { xs: "2.3rem", md: "4rem" },
                          mt: 1,
                        }}
                      >
                        {spotlight.name}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        sx={{ color: BRAND.muted, fontWeight: 900, mt: 1 }}
                      >
                        {spotlight.sku || "Catalog product"}
                      </MDTypography>
                      <MDTypography
                        variant="h4"
                        sx={{ color: BRAND.red, fontWeight: 950, mt: 2 }}
                      >
                        {priceText(resolvePrice(spotlight), currency)}
                      </MDTypography>
                      <MDBox display="flex" gap={1} flexWrap="wrap" mt={2.5}>
                        <MDButton
                          variant="contained"
                          sx={{
                            bgcolor: BRAND.red,
                            color: "#fff",
                            borderRadius: 2,
                            fontWeight: 950,
                            boxShadow: "none",
                          }}
                          onClick={() => navigate(toPath(`/catalog/${spotlight.documentId}`))}
                        >
                          View product
                        </MDButton>
                        <MDButton
                          variant="text"
                          sx={{ color: BRAND.ink, borderRadius: 2, fontWeight: 950 }}
                          onClick={() =>
                            canOrder
                              ? addProductToCart(spotlight)
                              : navigate("/authentication/sign-in?next=/catalog")
                          }
                        >
                          {canOrder ? "Add to cart" : "Sign in"}
                        </MDButton>
                      </MDBox>
                    </Box>
                  </Box>
                ) : null}

                {isLoading ? (
                  <MDBox textAlign="center" py={8}>
                    <MDTypography sx={{ color: BRAND.muted }}>Loading products...</MDTypography>
                  </MDBox>
                ) : visibleProducts.length === 0 ? (
                  <MDBox textAlign="center" py={8} sx={{ bgcolor: "#fff" }}>
                    <MDTypography sx={{ color: BRAND.muted }}>
                      {t("catalog.empty", "No products are available right now.")}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        xl: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1.5,
                    }}
                  >
                    {shelf.map((product, index) => {
                      const unitPrice = resolvePrice(product);
                      const hasPrice = Number(unitPrice) > 0;
                      return (
                        <Box
                          key={product.documentId || product.id}
                          sx={{
                            bgcolor: index % 5 === 0 ? BRAND.ink : "#fff",
                            color: index % 5 === 0 ? "#fff" : BRAND.ink,
                            minHeight: index % 4 === 0 ? 360 : 300,
                            p: 1.75,
                            display: "grid",
                            gridTemplateRows: "1fr auto",
                            boxShadow: "0 16px 46px rgba(16,24,40,0.08)",
                          }}
                        >
                          <Box
                            component={productImage(product) ? "img" : "div"}
                            src={productImage(product) || undefined}
                            sx={{
                              width: "100%",
                              height: "100%",
                              minHeight: 150,
                              objectFit: "contain",
                              bgcolor: index % 5 === 0 ? "rgba(255,255,255,0.08)" : "#EEF4F8",
                            }}
                          />
                          <Box sx={{ pt: 1.5 }}>
                            <MDTypography
                              variant="caption"
                              sx={{
                                color: index % 5 === 0 ? "rgba(255,255,255,0.58)" : BRAND.red,
                                fontWeight: 900,
                              }}
                            >
                              {product.sku || "Product"}
                            </MDTypography>
                            <MDTypography
                              variant="h6"
                              sx={{
                                color: index % 5 === 0 ? "#fff" : BRAND.ink,
                                fontWeight: 950,
                                lineHeight: 1.15,
                              }}
                            >
                              {product.name}
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1.5}>
                              <MDTypography
                                variant="button"
                                sx={{
                                  color: index % 5 === 0 ? "#fff" : hasPrice ? BRAND.red : BRAND.ink,
                                  fontWeight: 950,
                                }}
                              >
                                {priceText(unitPrice, currency)}
                              </MDTypography>
                              <Icon
                                onClick={() => navigate(toPath(`/catalog/${product.documentId}`))}
                                sx={{ cursor: "pointer", color: index % 5 === 0 ? "#fff" : BRAND.ink }}
                              >
                                arrow_forward
                              </Icon>
                            </MDBox>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (!isMobileRoute && storefrontTheme.preset === "industrial") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor:
              "radial-gradient(circle at 92% 5%, rgba(181,71,8,0.14), transparent 34%), #080B0E",
            py: 3,
          }}
        >
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <MDBox
                  sx={{
                    position: { md: "sticky" },
                    top: 116,
                    bgcolor: "rgba(255,255,255,0.07)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "24px",
                    p: 2.5,
                    boxShadow: "0 26px 80px rgba(0,0,0,0.22)",
                    backdropFilter: "blur(18px)",
                  }}
                >
                  <MDTypography
                    variant="overline"
                    sx={{
                      color: "rgba(255,255,255,0.58)",
                      fontWeight: 900,
                      letterSpacing: "0.14em",
                    }}
                  >
                    Technical catalog
                  </MDTypography>
                  <MDTypography
                    variant="h4"
                    sx={{ color: "#fff", fontWeight: 900, mt: 1 }}
                  >
                    {t("catalog.title", "Products")}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.68)",
                      mt: 1,
                      lineHeight: 1.55,
                    }}
                  >
                    {t("catalog.subtitle", "Search products by SKU or name.")}
                  </MDTypography>
                  <TextField
                    fullWidth
                    placeholder={t(
                      "common.searchPlaceholder",
                      "Search products...",
                    )}
                    value={q}
                    onChange={(event) => updateSearch(event.target.value)}
                    sx={{
                      mt: 2,
                      bgcolor: "rgba(255,255,255,0.08)",
                      borderRadius: "999px",
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        borderRadius: "999px",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.16)" },
                        "&:hover fieldset": { borderColor: BRAND.red },
                        "&.Mui-focused fieldset": { borderColor: BRAND.red },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon sx={{ color: BRAND.red }}>search</Icon>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <MDBox mt={2.5}>
                    <MDTypography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 900 }}
                    >
                      PLATFORM FILTER
                    </MDTypography>
                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                      <MDButton
                        variant={
                          !selectedTransmission ? "contained" : "outlined"
                        }
                        sx={{
                          justifyContent: "flex-start",
                          borderRadius: "999px",
                          bgcolor: !selectedTransmission
                            ? BRAND.red
                            : "transparent",
                          color: "#fff",
                          borderColor: "rgba(255,255,255,0.18)",
                          fontWeight: 900,
                        }}
                        onClick={() => setSelectedTransmission("")}
                      >
                        All platforms
                      </MDButton>
                      {transmissionOptions.map((transmission) => (
                        <MDButton
                          key={transmission}
                          variant={
                            selectedTransmission === transmission
                              ? "contained"
                              : "outlined"
                          }
                          sx={{
                            justifyContent: "flex-start",
                            borderRadius: "999px",
                            bgcolor:
                              selectedTransmission === transmission
                                ? BRAND.red
                                : "transparent",
                            color: "#fff",
                            borderColor: "rgba(255,255,255,0.18)",
                            fontWeight: 900,
                          }}
                          onClick={() => setSelectedTransmission(transmission)}
                        >
                          {transmission}
                        </MDButton>
                      ))}
                    </Stack>
                  </MDBox>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={9}>
                <MDBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.1fr 0.8fr 0.75fr 0.8fr 0.8fr",
                    gap: 1,
                    px: 2,
                    py: 1.2,
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "22px 22px 0 0",
                  }}
                >
                  {["Product", "SKU", "Platform", "Price", "Action"].map(
                    (label) => (
                      <MDTypography
                        key={label}
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.68)",
                          fontWeight: 900,
                          letterSpacing: "0.08em",
                        }}
                      >
                        {label}
                      </MDTypography>
                    ),
                  )}
                </MDBox>
                <MDBox
                  sx={{
                    bgcolor: "rgba(255,255,255,0.94)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderTop: 0,
                    borderRadius: "0 0 22px 22px",
                    overflow: "hidden",
                    boxShadow: "0 26px 80px rgba(0,0,0,0.20)",
                  }}
                >
                  {isLoading ? (
                    <MDBox textAlign="center" py={8}>
                      <MDTypography sx={{ color: BRAND.muted }}>
                        Loading products...
                      </MDTypography>
                    </MDBox>
                  ) : visibleProducts.length === 0 ? (
                    <MDBox textAlign="center" py={8}>
                      <MDTypography sx={{ color: BRAND.muted }}>
                        {t(
                          "catalog.empty",
                          "No products are available right now.",
                        )}
                      </MDTypography>
                    </MDBox>
                  ) : (
                    visibleProducts.map((product) => {
                      const unitPrice = resolvePrice(product);
                      const hasPrice = Number(unitPrice) > 0;
                      return (
                        <MDBox
                          key={product.documentId || product.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "1.1fr 0.8fr 0.75fr 0.8fr 0.8fr",
                            gap: 1,
                            alignItems: "center",
                            px: 1.25,
                            py: 1.1,
                            borderBottom: `1px solid ${BRAND.line}`,
                            transition: "background 160ms ease",
                            "&:hover": {
                              bgcolor: "rgba(181,71,8,0.055)",
                            },
                          }}
                        >
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1.25}
                            sx={{ minWidth: 0 }}
                          >
                            <Box
                              component={productImage(product) ? "img" : "div"}
                              src={productImage(product) || undefined}
                              sx={{
                                width: 58,
                                height: 48,
                                objectFit: "cover",
                                bgcolor: BRAND.canvas,
                                borderRadius: "12px",
                                flexShrink: 0,
                              }}
                            />
                            <MDTypography
                              variant="button"
                              sx={{
                                color: BRAND.ink,
                                fontWeight: 900,
                                lineHeight: 1.2,
                              }}
                            >
                              {product.name}
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: BRAND.muted,
                              fontFamily: "monospace",
                              fontWeight: 900,
                            }}
                          >
                            {product.sku || "PENDING"}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            {(product.transmissions || [])[0] || "Universal"}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            sx={{
                              color: hasPrice ? BRAND.red : BRAND.ink,
                              fontWeight: 900,
                            }}
                          >
                            {priceText(unitPrice, currency)}
                          </MDTypography>
                          <Stack direction="row" spacing={0.75}>
                            <MDButton
                              variant="outlined"
                              sx={{
                                borderRadius: 0,
                                color: BRAND.ink,
                                borderColor: BRAND.line,
                                fontWeight: 900,
                              }}
                              onClick={() =>
                                navigate(
                                  toPath(`/catalog/${product.documentId}`),
                                )
                              }
                            >
                              Details
                            </MDButton>
                            {hasPrice ? (
                              <MDButton
                                variant="contained"
                                sx={{
                                  borderRadius: 0,
                                  bgcolor: BRAND.red,
                                  color: "#fff",
                                  fontWeight: 900,
                                }}
                                onClick={() =>
                                  canOrder
                                    ? addProductToCart(product)
                                    : navigate(
                                        "/authentication/sign-in?next=/catalog",
                                      )
                                }
                              >
                                {canOrder ? "Add" : "Sign in"}
                              </MDButton>
                            ) : null}
                          </Stack>
                        </MDBox>
                      );
                    })
                  )}
                </MDBox>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (!isMobileRoute && storefrontTheme.preset === "commerce") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box sx={{ minHeight: "100vh", bgcolor: "#F7F9FC" }}>
          <Container maxWidth="xl" sx={{ pt: 4, pb: 2 }}>
            <MDBox
              sx={{
                bgcolor: "linear-gradient(135deg, #FFFFFF 0%, #EEF4F8 100%)",
                border: `1px solid ${BRAND.line}`,
                borderRadius: "36px",
                p: { xs: 2.5, md: 4 },
                boxShadow: "0 28px 90px rgba(16,24,40,0.10)",
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={5}>
                  <MDTypography
                    variant="h1"
                    sx={{
                      color: BRAND.ink,
                      fontWeight: 950,
                      fontSize: { xs: "2rem", md: "3.6rem" },
                      lineHeight: 0.98,
                    }}
                  >
                    {t("catalog.title", "Products")}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{ color: BRAND.muted, mt: 1.25, lineHeight: 1.65 }}
                  >
                    {t(
                      "catalog.subtitle",
                      "Search available products with current tenant pricing.",
                    )}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    placeholder={t(
                      "common.searchPlaceholder",
                      "Search products...",
                    )}
                    value={q}
                    onChange={(event) => updateSearch(event.target.value)}
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: "999px",
                      boxShadow: "0 18px 46px rgba(16,24,40,0.08)",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "999px",
                        fontSize: "1.05rem",
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: BRAND.red },
                        "&.Mui-focused fieldset": { borderColor: BRAND.red },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon sx={{ color: BRAND.muted }}>search</Icon>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </Container>
          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mb: 3, overflowX: "auto", pb: 0.25 }}
            >
              <Chip
                label={`All products (${products.length})`}
                clickable
                color={!selectedTransmission ? "error" : "default"}
                variant={!selectedTransmission ? "filled" : "outlined"}
                onClick={() => setSelectedTransmission("")}
                sx={{ flexShrink: 0, fontWeight: 900, borderRadius: "999px" }}
              />
              {transmissionOptions.map((transmission) => (
                <Chip
                  key={transmission}
                  label={transmission}
                  clickable
                  color={
                    selectedTransmission === transmission ? "error" : "default"
                  }
                  variant={
                    selectedTransmission === transmission
                      ? "filled"
                      : "outlined"
                  }
                  onClick={() => setSelectedTransmission(transmission)}
                  sx={{ flexShrink: 0, fontWeight: 900, borderRadius: "999px" }}
                />
              ))}
            </Stack>
            {isLoading ? (
              <MDBox textAlign="center" py={8}>
                <MDTypography sx={{ color: BRAND.muted }}>
                  Loading products...
                </MDTypography>
              </MDBox>
            ) : visibleProducts.length === 0 ? (
              <MDBox
                textAlign="center"
                py={8}
                sx={{ bgcolor: "#fff", borderRadius: "24px" }}
              >
                <MDTypography sx={{ color: BRAND.muted }}>
                  {t("catalog.empty", "No products are available right now.")}
                </MDTypography>
              </MDBox>
            ) : (
              <Grid container spacing={2.5}>
                {visibleProducts.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <ProductCard
                      product={product}
                      currency={currency}
                      canOrder={canOrder}
                      inCartQuantity={
                        cartQuantityByDocumentId.get(
                          String(product.documentId),
                        ) || 0
                      }
                      onAdd={() => addProductToCart(product)}
                      onSignIn={() =>
                        navigate(
                          `/authentication/sign-in?next=${encodeURIComponent(toPath(`/catalog/${product.documentId}`))}`,
                        )
                      }
                      onOpen={() =>
                        navigate(toPath(`/catalog/${product.documentId}`))
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout maxWidth={null}>
      <Box
        sx={{ minHeight: "100vh", bgcolor: isMobileRoute ? "#fbfbfa" : "#fff" }}
      >
        {isMobileRoute ? (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              bgcolor: "rgba(251,251,250,0.92)",
              backdropFilter: "blur(18px)",
              borderBottom: `1px solid ${BRAND.line}`,
            }}
          >
            <Container maxWidth="sm" sx={{ px: 2, py: 1 }}>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1.25}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="vbspecialties.com"
                  sx={{ width: 78, height: "auto", display: "block" }}
                  onClick={() => navigate("/m")}
                />
                <Stack direction="row" spacing={0.75}>
                  {[
                    ["home", () => navigate("/m"), "Home"],
                    ...(canOrder
                      ? [["shopping_cart", () => navigate("/m/cart"), "Cart"]]
                      : []),
                    [
                      token ? "person" : "login",
                      () =>
                        navigate(
                          token
                            ? "/m/account"
                            : "/m/authentication/sign-in?next=/m/catalog",
                        ),
                      token ? "Account" : "Sign in",
                    ],
                  ].map(([icon, onClick, label]) => (
                    <MDButton
                      key={label}
                      variant="outlined"
                      sx={{
                        minWidth: 44,
                        width: 44,
                        height: 44,
                        p: 0,
                        borderRadius: 1.25,
                        bgcolor:
                          label === "Account" || label === "Sign in"
                            ? BRAND.ink
                            : "#fff",
                        color:
                          label === "Account" || label === "Sign in"
                            ? "#fff"
                            : BRAND.ink,
                        borderColor:
                          label === "Account" || label === "Sign in"
                            ? BRAND.ink
                            : BRAND.line,
                        boxShadow: "none",
                      }}
                      onClick={onClick}
                      aria-label={label}
                    >
                      {label === "Cart" ? (
                        <Badge
                          badgeContent={itemCount || 0}
                          color="error"
                          invisible={!itemCount}
                        >
                          <Icon sx={{ fontSize: 24 }}>{icon}</Icon>
                        </Badge>
                      ) : (
                        <Icon sx={{ fontSize: 24 }}>{icon}</Icon>
                      )}
                    </MDButton>
                  ))}
                </Stack>
              </MDBox>
            </Container>
          </Box>
        ) : null}
        <MDBox
          sx={{
            bgcolor: isMobileRoute ? "#fbfbfa" : BRAND.canvas,
            borderBottom: isMobileRoute ? "none" : `1px solid ${BRAND.line}`,
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              py: { xs: isMobileRoute ? 2 : 5, md: 7 },
              px: { xs: 2, md: 3 },
            }}
          >
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={7}>
                <MDTypography
                  variant="overline"
                  sx={{
                    color: BRAND.red,
                    fontWeight: 900,
                    letterSpacing: isMobileRoute ? 0 : "0.14em",
                  }}
                >
                  {t("common.catalog", "Catalog")}
                </MDTypography>
                <MDTypography
                  variant={isMobileRoute ? "h3" : "h1"}
                  sx={{
                    color: BRAND.ink,
                    fontWeight: 900,
                    fontSize: {
                      xs: isMobileRoute ? "1.55rem" : "2.35rem",
                      md: "4rem",
                    },
                    lineHeight: 1.04,
                    letterSpacing: 0,
                    mt: 0.75,
                  }}
                >
                  {t("catalog.title", "Find the right product.")}
                </MDTypography>
                {!isMobileRoute ? (
                  <MDTypography
                    variant="body1"
                    sx={{
                      color: BRAND.muted,
                      mt: 1.25,
                      lineHeight: 1.6,
                      maxWidth: 620,
                    }}
                  >
                    {t("catalog.subtitle", "Search products by SKU or name.")}
                  </MDTypography>
                ) : null}
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder={t(
                    "common.searchPlaceholder",
                    "Search products...",
                  )}
                  value={q}
                  onChange={(event) => updateSearch(event.target.value)}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 1,
                    boxShadow: isMobileRoute
                      ? "none"
                      : "0 16px 44px rgba(17,24,39,0.06)",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      fontSize: "0.95rem",
                      "& fieldset": { borderColor: "rgba(17,24,39,0.12)" },
                      "&:hover fieldset": {
                        borderColor: "rgba(215,38,30,0.35)",
                      },
                      "&.Mui-focused fieldset": { borderColor: BRAND.red },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: BRAND.muted }}>search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </MDBox>

        <Container
          maxWidth="lg"
          sx={{
            py: { xs: isMobileRoute ? 0 : 5, md: 7 },
            px: { xs: 2, md: 3 },
          }}
        >
          {!canOrder && isMobileRoute ? (
            <MDBox
              sx={{
                borderTop: `1px solid ${BRAND.line}`,
                borderBottom: `1px solid ${BRAND.line}`,
                py: 1.25,
                mb: 1.5,
              }}
            >
              <MDTypography
                variant="body2"
                sx={{ color: BRAND.ink, fontWeight: 900 }}
              >
                Retail pricing shown for your region.
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ color: BRAND.muted, fontWeight: 700 }}
              >
                Sign in to order and use cart checkout.
              </MDTypography>
            </MDBox>
          ) : null}
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            mb={isMobileRoute ? 1.5 : 3}
          >
            <MDTypography
              variant="body2"
              sx={{ color: BRAND.muted, fontWeight: 700 }}
            >
              {isLoading
                ? "Loading catalog"
                : `${visibleProducts.length} product${
                    visibleProducts.length === 1 ? "" : "s"
                  }`}
              {debouncedSearch ? ` matching "${debouncedSearch}"` : ""}
            </MDTypography>
            {debouncedSearch ? (
              <MDButton
                variant="text"
                sx={{ color: BRAND.red, fontWeight: 900 }}
                onClick={() => updateSearch("")}
              >
                Clear
              </MDButton>
            ) : null}
          </MDBox>
          {transmissionOptions.length ? (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mb: 2.5, overflowX: "auto", pb: 0.25 }}
            >
              <Chip
                label="All"
                clickable
                color={!selectedTransmission ? "error" : "default"}
                variant={!selectedTransmission ? "filled" : "outlined"}
                onClick={() => setSelectedTransmission("")}
                sx={{ flexShrink: 0, fontWeight: 800 }}
              />
              {transmissionOptions.map((transmission) => (
                <Chip
                  key={transmission}
                  label={transmission}
                  clickable
                  color={
                    selectedTransmission === transmission ? "error" : "default"
                  }
                  variant={
                    selectedTransmission === transmission
                      ? "filled"
                      : "outlined"
                  }
                  onClick={() => setSelectedTransmission(transmission)}
                  sx={{ flexShrink: 0, fontWeight: 800 }}
                />
              ))}
            </Stack>
          ) : null}

          {isLoading ? (
            <MDBox textAlign="center" py={8}>
              <MDTypography sx={{ color: BRAND.muted }}>
                Loading products...
              </MDTypography>
            </MDBox>
          ) : visibleProducts.length === 0 ? (
            <MDBox
              textAlign="center"
              py={8}
              sx={{
                borderTop: `1px solid ${BRAND.line}`,
                borderBottom: `1px solid ${BRAND.line}`,
                bgcolor: "#fff",
              }}
            >
              <MDTypography
                variant="h6"
                sx={{ color: BRAND.ink, fontWeight: 900, mb: 1 }}
              >
                No products found
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: BRAND.muted }}>
                {debouncedSearch
                  ? "Try a different search term."
                  : "Check back soon for updated inventory."}
              </MDTypography>
            </MDBox>
          ) : (
            <Grid container spacing={isMobileRoute ? 1.15 : 3}>
              {visibleProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <ProductCard
                    product={product}
                    isMobile={isMobileRoute}
                    currency={currency}
                    canOrder={canOrder}
                    inCartQuantity={
                      cartQuantityByDocumentId.get(
                        String(product.documentId),
                      ) || 0
                    }
                    onAdd={() => addProductToCart(product)}
                    onSignIn={() =>
                      navigate(
                        `/m/authentication/sign-in?next=${encodeURIComponent(
                          toPath(`/catalog/${product.documentId}`),
                        )}`,
                      )
                    }
                    onOpen={() =>
                      navigate(toPath(`/catalog/${product.documentId}`))
                    }
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>

        <MDBox
          sx={{
            bgcolor: BRAND.ink,
            color: "#fff",
            py: 4,
            mt: 4,
            display: isMobileRoute ? "none" : "block",
          }}
        >
          <Container maxWidth="lg">
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <MDTypography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.72)" }}
              >
                © {new Date().getFullYear()}{" "}
                {t("common.brandFallback", "Storefront")}
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="text"
                  sx={{ color: "#fff", fontWeight: 900 }}
                  onClick={() => navigate(toPath("/"))}
                >
                  Home
                </MDButton>
                <MDButton
                  variant="text"
                  sx={{ color: "#fff", fontWeight: 900 }}
                  onClick={() => navigate(toPath("/authentication/sign-in"))}
                >
                  Sign in
                </MDButton>
              </MDBox>
            </MDBox>
          </Container>
        </MDBox>
      </Box>
    </StorefrontLayout>
  );
}
